import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  Divider,
  Switch,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  Tooltip,
  Collapse,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import LockIcon from '@mui/icons-material/Lock';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import SecurityIcon from '@mui/icons-material/Security';
import {
  restaurantWaiterApi,
  RestaurantWaiterDto,
  CreateRestaurantWaiterDto,
  UpdateRestaurantWaiterDto,
} from '../../../../services/restaurantApiService';
import { useTranslation } from 'react-i18next';

interface RestaurantWaitersTabProps {
  restaurantScreenId: number;
}

const RestaurantWaitersTab: React.FC<RestaurantWaitersTabProps> = ({ restaurantScreenId }) => {
  const { t } = useTranslation('salesSystem');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [waiters, setWaiters] = useState<RestaurantWaiterDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [showPermissions, setShowPermissions] = useState(false); // Yetkiler default gizli

  // Form state
  const [isEditing, setIsEditing] = useState(false);
  const [editingWaiterId, setEditingWaiterId] = useState<number | null>(null);
  const [waiterName, setWaiterName] = useState('');
  const [waiterPin, setWaiterPin] = useState('');
  const [canCloseSale, setCanCloseSale] = useState(true);
  const [canCloseTable, setCanCloseTable] = useState(true);
  const [canAddProduct, setCanAddProduct] = useState(true);
  const [canRemoveProduct, setCanRemoveProduct] = useState(true);
  const [canClearCart, setCanClearCart] = useState(true);
  const [canPrintToKitchen, setCanPrintToKitchen] = useState(true);
  const [canDeletePrintedItems, setCanDeletePrintedItems] = useState(false);
  const [canDeleteUnprintedItems, setCanDeleteUnprintedItems] = useState(false); // Yazdırılmamış ürün silme yetkisi (varsayılan KAPALI)
  const [canTransferTable, setCanTransferTable] = useState(true);
  const [canPosRefund, setCanPosRefund] = useState(false);
  const [canCancelReceipt, setCanCancelReceipt] = useState(false); // Adisyon iptal yetkisi (varsayılan KAPALI)
  const [canCancelUnprintedReceipt, setCanCancelUnprintedReceipt] = useState(false); // Yazdırılmamış adisyon iptal yetkisi (varsayılan KAPALI)
  const [canPrintReceipt, setCanPrintReceipt] = useState(false); // Adisyon yazdırma yetkisi (varsayılan KAPALI)
  const [canAddProductAfterReceiptPrint, setCanAddProductAfterReceiptPrint] = useState(false); // Adisyon sonrası ürün ekleme (varsayılan KAPALI)
  const [canUsePavoP2P, setCanUsePavoP2P] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [showPin, setShowPin] = useState(false);

  // Delete confirmation
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [waiterToDelete, setWaiterToDelete] = useState<RestaurantWaiterDto | null>(null);

  // Garsonları yükle
  const loadWaiters = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await restaurantWaiterApi.getByRestaurantScreenId(restaurantScreenId);
      
      // DEBUG LOG - API'den gelen garsonlar
      console.log('📥 LOAD WAITERS - API\'den gelen:', data.map(w => ({
        Id: w.Id,
        Name: w.WaiterName,
        CanCancelReceipt: w.CanCancelReceipt,
        CanPrintReceipt: w.CanPrintReceipt,
        CanClearCart: w.CanClearCart,
        // ✅ YENI: API'den geri donen Adisyon sonrasi urun ekleme yetkisini AYRI logla
        // Eger BU FIELD undefined ise => Backend DTO/binary deploy edilmemis
        // Eger HEP false ise => DB kolonu yok veya update sirasinda gelmiyor
        CanAddProductAfterReceiptPrint: (w as any).CanAddProductAfterReceiptPrint,
      })));
      
      setWaiters(data);
    } catch (err: any) {
      console.error('Garsonlar yüklenirken hata:', err);
      setError(t('restaurantWaitersTab.errors.loadFailed', { message: err.response?.data?.Message || err.message }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadWaiters();
  }, [restaurantScreenId]);

  // Form temizle
  const resetForm = () => {
    setIsEditing(false);
    setEditingWaiterId(null);
    setWaiterName('');
    setWaiterPin('');
    setCanCloseSale(true);
    setCanCloseTable(true);
    setCanAddProduct(true);
    setCanRemoveProduct(true);
    setCanClearCart(true);
    setCanPrintToKitchen(true);
    setCanDeletePrintedItems(false);
    setCanDeleteUnprintedItems(false); // Varsayılan KAPALI
    setCanTransferTable(true);
    setCanPosRefund(false);
    setCanCancelReceipt(false); // Varsayılan KAPALI
    setCanCancelUnprintedReceipt(false); // Varsayılan KAPALI
    setCanPrintReceipt(false); // Varsayılan KAPALI
    setCanAddProductAfterReceiptPrint(false); // Varsayılan KAPALI
    setCanUsePavoP2P(false);
    setIsActive(true);
    setShowPin(false);
    setError(null);
    setSuccessMessage(null);
  };

  // Yeni garson ekle
  const handleAdd = async () => {
    try {
      setError(null);
      setSuccessMessage(null);

      if (!waiterName.trim()) {
        setError(t('restaurantWaitersTab.errors.nameRequired'));
        return;
      }

      if (waiterPin.length < 4) {
        setError(t('restaurantWaitersTab.errors.pinLength'));
        return;
      }

      const newWaiter: CreateRestaurantWaiterDto = {
        RestaurantScreenId: restaurantScreenId,
        WaiterName: waiterName.trim(),
        WaiterPin: waiterPin,
        CanCloseSale: canCloseSale,
        CanCloseTable: canCloseTable,
        CanAddProduct: canAddProduct,
        CanRemoveProduct: canRemoveProduct,
        CanClearCart: canClearCart,
        CanPrintToKitchen: canPrintToKitchen,
        CanDeletePrintedItems: canDeletePrintedItems,
        CanDeleteUnprintedItems: canDeleteUnprintedItems,
        CanTransferTable: canTransferTable,
        CanPosRefund: canPosRefund,
        CanCancelReceipt: canCancelReceipt,
        CanCancelUnprintedReceipt: canCancelUnprintedReceipt,
        CanPrintReceipt: canPrintReceipt,
        CanAddProductAfterReceiptPrint: canAddProductAfterReceiptPrint,
        CanUsePavoP2P: canUsePavoP2P,
        IsActive: isActive,
      };

      // DEBUG LOG - API'ye gönderilen veri
      console.log('📤 CREATE WAITER - Gönderilen:', {
        CanCancelReceipt: newWaiter.CanCancelReceipt,
        CanPrintReceipt: newWaiter.CanPrintReceipt,
        CanClearCart: newWaiter.CanClearCart,
        fullData: newWaiter
      });

      await restaurantWaiterApi.create(newWaiter);
      setSuccessMessage(t('restaurantWaitersTab.messages.added'));
      await loadWaiters();
      resetForm();
    } catch (err: any) {
      console.error('Garson eklenirken hata:', err);
      setError(t('restaurantWaitersTab.errors.addFailed', { message: err.response?.data?.Message || err.message }));
    }
  };

  // Garson düzenle
  const handleEdit = (waiter: RestaurantWaiterDto) => {
    // DEBUG LOG - DB'den gelen garson verisi
    console.log('📥 EDIT WAITER - DB\'den gelen:', {
      Id: waiter.Id,
      Name: waiter.WaiterName,
      CanCancelReceipt: waiter.CanCancelReceipt,
      CanPrintReceipt: waiter.CanPrintReceipt,
      CanClearCart: waiter.CanClearCart,
      fullData: waiter
    });
    
    setIsEditing(true);
    setEditingWaiterId(waiter.Id);
    setWaiterName(waiter.WaiterName);
    setWaiterPin(waiter.WaiterPin);
    setCanCloseSale(waiter.CanCloseSale);
    setCanCloseTable(waiter.CanCloseTable);
    setCanAddProduct(waiter.CanAddProduct);
    setCanRemoveProduct(waiter.CanRemoveProduct);
    setCanClearCart(waiter.CanClearCart);
    setCanPrintToKitchen(waiter.CanPrintToKitchen);
    setCanDeletePrintedItems(waiter.CanDeletePrintedItems);
    setCanDeleteUnprintedItems(waiter.CanDeleteUnprintedItems ?? false); // NULL ise kapalı
    setCanTransferTable(waiter.CanTransferTable ?? true);
    setCanPosRefund(waiter.CanPosRefund ?? false);
    setCanCancelReceipt(waiter.CanCancelReceipt ?? false); // NULL ise kapalı
    setCanCancelUnprintedReceipt(waiter.CanCancelUnprintedReceipt ?? false); // NULL ise kapalı
    setCanPrintReceipt(waiter.CanPrintReceipt ?? false); // NULL ise kapalı
    setCanAddProductAfterReceiptPrint(waiter.CanAddProductAfterReceiptPrint ?? false); // NULL ise kapalı
    setCanUsePavoP2P(waiter.CanUsePavoP2P ?? false);
    setIsActive(waiter.IsActive);
    setError(null);
    setSuccessMessage(null);
  };

  // Garson güncelle
  const handleUpdate = async () => {
    try {
      setError(null);
      setSuccessMessage(null);

      if (!editingWaiterId) return;

      if (!waiterName.trim()) {
        setError(t('restaurantWaitersTab.errors.nameRequired'));
        return;
      }

      if (waiterPin.length < 4) {
        setError(t('restaurantWaitersTab.errors.pinLength'));
        return;
      }

      const updateData: UpdateRestaurantWaiterDto = {
        Id: editingWaiterId,
        WaiterName: waiterName.trim(),
        WaiterPin: waiterPin,
        CanCloseSale: canCloseSale,
        CanCloseTable: canCloseTable,
        CanAddProduct: canAddProduct,
        CanRemoveProduct: canRemoveProduct,
        CanClearCart: canClearCart,
        CanPrintToKitchen: canPrintToKitchen,
        CanDeletePrintedItems: canDeletePrintedItems,
        CanDeleteUnprintedItems: canDeleteUnprintedItems,
        CanTransferTable: canTransferTable,
        CanPosRefund: canPosRefund,
        CanCancelReceipt: canCancelReceipt,
        CanCancelUnprintedReceipt: canCancelUnprintedReceipt,
        CanPrintReceipt: canPrintReceipt,
        CanAddProductAfterReceiptPrint: canAddProductAfterReceiptPrint,
        CanUsePavoP2P: canUsePavoP2P,
        IsActive: isActive,
      };

      // DEBUG LOG - API'ye gönderilen veri
      console.log('📤 UPDATE WAITER - Gönderilen:', {
        Id: updateData.Id,
        CanCancelReceipt: updateData.CanCancelReceipt,
        CanPrintReceipt: updateData.CanPrintReceipt,
        CanClearCart: updateData.CanClearCart,
        // ✅ YENI: Adisyon sonrasi urun ekleme yetkisini AYRI logla
        CanAddProductAfterReceiptPrint: updateData.CanAddProductAfterReceiptPrint,
        fullData: updateData,
      });

      await restaurantWaiterApi.update(editingWaiterId, updateData);
      setSuccessMessage(t('restaurantWaitersTab.messages.updated'));
      await loadWaiters();
      resetForm();
    } catch (err: any) {
      console.error('Garson güncellenirken hata:', err);
      setError(t('restaurantWaitersTab.errors.updateFailed', { message: err.response?.data?.Message || err.message }));
    }
  };

  // Silme modalı aç
  const handleDeleteClick = (waiter: RestaurantWaiterDto) => {
    setWaiterToDelete(waiter);
    setDeleteConfirmOpen(true);
  };

  // Garson sil
  const handleDeleteConfirm = async () => {
    if (!waiterToDelete) return;

    try {
      setError(null);
      await restaurantWaiterApi.delete(waiterToDelete.Id);
      setSuccessMessage(t('restaurantWaitersTab.messages.deleted'));
      await loadWaiters();
      setDeleteConfirmOpen(false);
      setWaiterToDelete(null);
      resetForm();
    } catch (err: any) {
      console.error('Garson silinirken hata:', err);
      setError(t('restaurantWaitersTab.errors.deleteFailed', { message: err.response?.data?.Message || err.message }));
      setDeleteConfirmOpen(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, height: '100%' }}>
      {/* Üst/Sol Panel - Form */}
      <Paper elevation={0} sx={{ p: 2, border: '1px solid #e0e0e0', flex: 1, maxHeight: { xs: 'none', md: 450 }, overflowY: { xs: 'visible', md: 'auto' } }}>
        <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 'bold' }}>
          <LockIcon fontSize="small" /> {isEditing ? t('restaurantWaitersTab.form.editTitle') : t('restaurantWaitersTab.form.newTitle')}
        </Typography>
        <Divider sx={{ mb: 2 }} />

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {successMessage && <Alert severity="success" sx={{ mb: 2 }}>{successMessage}</Alert>}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Box sx={{ display: 'flex', gap: 1, flexDirection: { xs: 'column', sm: 'row' } }}>
            <TextField
              label={t('restaurantWaitersTab.form.name')}
              variant="outlined"
              size="small"
              value={waiterName}
              onChange={(e) => setWaiterName(e.target.value)}
              placeholder={t('restaurantWaitersTab.form.namePlaceholder')}
              sx={{ flex: 1 }}
              fullWidth={isMobile}
            />

            <TextField
              label={t('restaurantWaitersTab.form.pin')}
              variant="outlined"
              size="small"
              type={showPin ? 'text' : 'password'}
              value={waiterPin}
              onChange={(e) => setWaiterPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder={t('restaurantWaitersTab.form.pinPlaceholder')}
              sx={{ width: { xs: '100%', sm: '120px' } }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPin(!showPin)}
                      edge="end"
                      size="small"
                    >
                      {showPin ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {/* Yetkiler Butonu */}
          <Button
            variant="outlined"
            size="small"
            onClick={() => setShowPermissions(!showPermissions)}
            startIcon={<SecurityIcon />}
            endIcon={showPermissions ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            sx={{ alignSelf: 'flex-start' }}
          >
            {t('restaurantWaitersTab.form.permissions')} {showPermissions ? t('restaurantWaitersTab.form.hide') : t('restaurantWaitersTab.form.show')}
          </Button>

          {/* Yetkiler - Collapse */}
          <Collapse in={showPermissions}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={canCloseSale}
                  onChange={(e) => setCanCloseSale(e.target.checked)}
                  color="primary"
                  size="small"
                />
              }
              label={<Typography variant="body2">{t('restaurantWaitersTab.permissions.canCloseSale')}</Typography>}
              sx={{ m: 0 }}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={canCloseTable}
                  onChange={(e) => setCanCloseTable(e.target.checked)}
                  color="warning"
                  size="small"
                />
              }
              label={<Typography variant="body2">{t('restaurantWaitersTab.permissions.canCloseTable')}</Typography>}
              sx={{ m: 0 }}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={canAddProduct}
                  onChange={(e) => setCanAddProduct(e.target.checked)}
                  color="primary"
                  size="small"
                />
              }
              label={<Typography variant="body2">{t('restaurantWaitersTab.permissions.canAddProduct')}</Typography>}
              sx={{ m: 0 }}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={canRemoveProduct}
                  onChange={(e) => setCanRemoveProduct(e.target.checked)}
                  color="primary"
                  size="small"
                />
              }
              label={<Typography variant="body2">{t('restaurantWaitersTab.permissions.canRemoveProduct')}</Typography>}
              sx={{ m: 0 }}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={canDeletePrintedItems}
                  onChange={(e) => setCanDeletePrintedItems(e.target.checked)}
                  color="warning"
                  size="small"
                  disabled={!canRemoveProduct}
                />
              }
              label={<Typography variant="body2" color={!canRemoveProduct ? 'text.disabled' : 'text.secondary'}>{t('restaurantWaitersTab.permissions.canDeletePrintedItems')}</Typography>}
              sx={{ m: 0, ml: 2 }}
            />

            {/* ✅ YENİ: Yazdırılmamış ürün silme — canRemoveProduct'tan BAĞIMSIZ.
                Genel silme yetkisi olmayan garsona, mutfağa yazdırılmamış
                satırları silme hakkı verir. */}
            <FormControlLabel
              control={
                <Switch
                  checked={canDeleteUnprintedItems}
                  onChange={(e) => setCanDeleteUnprintedItems(e.target.checked)}
                  color="warning"
                  size="small"
                />
              }
              label={<Typography variant="body2">{t('restaurantWaitersTab.permissions.canDeleteUnprintedItems', 'Yazdırılmamış Ürünleri Silebilir (mutfağa gitmemiş)')}</Typography>}
              sx={{ m: 0 }}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={canClearCart}
                  onChange={(e) => setCanClearCart(e.target.checked)}
                  color="primary"
                  size="small"
                />
              }
              label={<Typography variant="body2">{t('restaurantWaitersTab.permissions.canClearCart')}</Typography>}
              sx={{ m: 0 }}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={canPrintToKitchen}
                  onChange={(e) => setCanPrintToKitchen(e.target.checked)}
                  color="primary"
                  size="small"
                />
              }
              label={<Typography variant="body2">{t('restaurantWaitersTab.permissions.canPrintToKitchen')}</Typography>}
              sx={{ m: 0 }}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={canTransferTable}
                  onChange={(e) => setCanTransferTable(e.target.checked)}
                  color="secondary"
                  size="small"
                />
              }
              label={<Typography variant="body2">{t('restaurantWaitersTab.permissions.canTransferTable')}</Typography>}
              sx={{ m: 0 }}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={canPosRefund}
                  onChange={(e) => setCanPosRefund(e.target.checked)}
                  color="error"
                  size="small"
                />
              }
              label={<Typography variant="body2">{t('restaurantWaitersTab.permissions.canPosRefund')}</Typography>}
              sx={{ m: 0 }}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={canClearCart}
                  onChange={(e) => setCanClearCart(e.target.checked)}
                  color="warning"
                  size="small"
                />
              }
              label={<Typography variant="body2">{t('restaurantWaitersTab.permissions.canClearCartSimple')}</Typography>}
              sx={{ m: 0 }}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={canCancelReceipt}
                  onChange={(e) => setCanCancelReceipt(e.target.checked)}
                  color="error"
                  size="small"
                />
              }
              label={<Typography variant="body2">{t('restaurantWaitersTab.permissions.canCancelReceipt')}</Typography>}
              sx={{ m: 0 }}
            />

            {/* ✅ YENİ: Yazdırılmamış adisyon iptali — masada mutfağa yazdırılmış
                satır yoksa adisyonu iptal edebilir. Yazdırılmış satır varsa
                yukarıdaki genel Adisyon İptal yetkisi gerekir. */}
            <FormControlLabel
              control={
                <Switch
                  checked={canCancelUnprintedReceipt}
                  onChange={(e) => setCanCancelUnprintedReceipt(e.target.checked)}
                  color="error"
                  size="small"
                />
              }
              label={<Typography variant="body2">{t('restaurantWaitersTab.permissions.canCancelUnprintedReceipt', 'Yazdırılmamış Adisyonu İptal Edebilir (mutfağa gitmemiş)')}</Typography>}
              sx={{ m: 0, ml: 2 }}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={canPrintReceipt}
                  onChange={(e) => setCanPrintReceipt(e.target.checked)}
                  color="info"
                  size="small"
                />
              }
              label={<Typography variant="body2">{t('restaurantWaitersTab.permissions.canPrintReceipt')}</Typography>}
              sx={{ m: 0 }}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={canAddProductAfterReceiptPrint}
                  onChange={(e) => setCanAddProductAfterReceiptPrint(e.target.checked)}
                  color="warning"
                  size="small"
                />
              }
              label={<Typography variant="body2">{t('restaurantWaitersTab.permissions.canAddProductAfterReceiptPrint')}</Typography>}
              sx={{ m: 0 }}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={canUsePavoP2P}
                  onChange={(e) => setCanUsePavoP2P(e.target.checked)}
                  color="secondary"
                  size="small"
                />
              }
              label={<Typography variant="body2">{t('restaurantWaitersTab.permissions.canUsePavoP2P')}</Typography>}
              sx={{ m: 0 }}
            />
            </Box>
          </Collapse>

          <FormControlLabel
            control={
              <Switch
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                color="primary"
                size="small"
              />
            }
            label={<Typography variant="body2">{t('restaurantWaitersTab.form.active')}</Typography>}
            sx={{ mt: 0.5, m: 0 }}
          />

          <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
            {isEditing ? (
              <>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleUpdate}
                  fullWidth
                >
                  {t('restaurantWaitersTab.form.update')}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<CancelIcon />}
                  onClick={resetForm}
                  fullWidth
                >
                  {t('restaurantWaitersTab.common.cancel')}
                </Button>
              </>
            ) : (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAdd}
                fullWidth
              >
                {t('restaurantWaitersTab.form.add')}
              </Button>
            )}
          </Box>
        </Box>
      </Paper>

      {/* Alt/Sağ Panel - Liste */}
      <Paper elevation={0} sx={{ p: 2, border: '1px solid #e0e0e0', flex: 1, display: 'flex', flexDirection: 'column', maxHeight: { xs: 'none', md: 450 } }}>
        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
          {t('restaurantWaitersTab.list.title', { count: waiters.length })}
        </Typography>
        <Divider sx={{ mb: 1 }} />

        {waiters.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
            {t('restaurantWaitersTab.list.empty')}
          </Typography>
        ) : (
          <List dense sx={{ overflowY: { xs: 'visible', md: 'auto' }, flexGrow: 1, maxHeight: { xs: 'none', md: 350 } }}>
            {waiters.map((waiter) => (
              <ListItem
                key={waiter.Id}
                sx={{
                  border: '1px solid #e0e0e0',
                  borderRadius: 1,
                  mb: 1,
                  bgcolor: !waiter.IsActive ? '#f5f5f5' : 'white',
                }}
              >
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="subtitle2" fontWeight="bold">
                        {waiter.WaiterName}
                      </Typography>
                      {!waiter.IsActive && (
                        <Typography variant="caption" color="error">
                          ({t('restaurantWaitersTab.form.passive')})
                        </Typography>
                      )}
                    </Box>
                  }
                  secondary={
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, mt: 0.5 }}>
                      <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                        🔒 PIN: {waiter.WaiterPin}
                      </Typography>
                      <Typography variant="caption" sx={{ fontSize: '0.65rem', lineHeight: 1.2 }}>
                        {[
                          waiter.CanCloseSale && `💰${t('restaurantWaitersTab.permissionSummary.account')}`,
                          waiter.CanCloseTable && `🚪${t('restaurantWaitersTab.permissionSummary.table')}`,
                          waiter.CanAddProduct && `➕${t('restaurantWaitersTab.permissionSummary.add')}`,
                          waiter.CanRemoveProduct && `➖${t('restaurantWaitersTab.permissionSummary.remove')}`,
                          waiter.CanClearCart && `🗑️${t('restaurantWaitersTab.permissionSummary.clear')}`,
                          waiter.CanPrintToKitchen && `🖨️${t('restaurantWaitersTab.permissionSummary.print')}`,
                          waiter.CanDeletePrintedItems && `⚠️${t('restaurantWaitersTab.permissionSummary.deletePrinted')}`,
                          waiter.CanTransferTable && `🔄${t('restaurantWaitersTab.permissionSummary.transfer')}`
                        ].filter(Boolean).join(' • ') || t('restaurantWaitersTab.permissionSummary.none')}
                      </Typography>
                    </Box>
                  }
                />
                <ListItemSecondaryAction>
                  <Tooltip title={t('restaurantWaitersTab.actions.edit')}>
                    <IconButton edge="end" size="small" onClick={() => handleEdit(waiter)} sx={{ mr: 0.5 }}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={t('restaurantWaitersTab.actions.delete')}>
                    <IconButton edge="end" size="small" color="error" onClick={() => handleDeleteClick(waiter)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        )}
      </Paper>

      {/* Silme Onay Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>{t('restaurantWaitersTab.deleteDialog.title')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('restaurantWaitersTab.deleteDialog.description', { name: waiterToDelete?.WaiterName })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>{t('restaurantWaitersTab.common.cancel')}</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            {t('restaurantWaitersTab.actions.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RestaurantWaitersTab;

